import { Link } from "react-router";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextLink } from "@/components/ui/TextLink";
import { Reveal } from "@/components/marketing/Reveal";
import { PageHero, Section, SectionIntro } from "@/components/marketing/Section";

/**
 * Company, and the contact section the rest of the site links to.
 *
 * One page rather than an About page plus a Contact page, because there is one
 * paragraph of company to tell and a form-free contact block. Splitting them
 * would produce two thin pages instead of one honest one.
 *
 * **No contact form.** A form here would post to an endpoint that does not
 * exist - the API has no messaging route and no mail service beyond the auth
 * provider's transactional templates. A mailto address works today, so it is
 * what the page offers.
 */

const PRINCIPLES = [
  {
    title: "The app never blocks work",
    body: "It warns, records and escalates. A safety tool that stops a job gets bypassed within a week, and a bypassed tool records nothing at all. Every feature is designed around being ignorable and useful anyway.",
  },
  {
    title: "Ninety seconds is a hard budget",
    body: "Not a target. A check that runs long is a check that gets skipped on the day it would have mattered, so anything that cannot fit in the technician's time does not ship.",
  },
  {
    title: "Say no in public",
    body: "There is a page on this site listing what the product will not do. Over-promising on physics is how a safety product loses the crew, and the crew is the only user who matters.",
  },
  {
    title: "Offline is the normal case",
    body: "Not a degraded mode. The work happens at the bottom of a right-of-way and in a plant room two floors down, so “saved, will sync” is a success message and never an error.",
  },
];

export function Company() {
  return (
    <>
      <PageHero
        eyebrow="Company"
        title="TaskIQ is built by Visionify."
        lead="Visionify builds computer-vision safety products for industrial and field workforces. The Visionify Safety Platform watches fixed sites through the cameras already installed on them. TaskIQ is the other half of that problem: the work that happens at an address nobody owns, with no camera on the wall and nothing to install."
      />

      {/* ── Why it exists ─────────────────────────────────────────────────── */}
      <Section>
        <div className="split">
          <SectionIntro
            size="sm"
            eyebrow="Why we built it"
            title="Fixed-camera analytics cannot see the eleventh service address of the day."
          >
            <div className="company-copy">
              <p>
                Everything we had built until now assumed a site you control: a wall to mount a
                camera on, a network to put it on, and a space that looks the same tomorrow. That
                covers plants and warehouses well, and it covers field work not at all.
              </p>
              <p>
                Field crews are the population with the worst injury exposure and the least
                instrumentation. They also already carry a device with a camera, an inclinometer and
                a GPS in it. TaskIQ is what happens when you stop trying to install infrastructure
                and start using the phone.
              </p>
            </div>
          </SectionIntro>

          <Reveal delay={80}>
            <Card padding="lg">
              <p className="eyebrow eyebrow--sm eyebrow--subtle">Also from Visionify</p>
              <div className="also-from">
                <h3 className="blurb__title blurb__title--lg">Visionify Safety Platform</h3>
                <p className="blurb__body blurb__body--lg">
                  Continuous safety monitoring for sites you own, running on the cameras already
                  installed on them. If your problem is a fixed site rather than a route, that is
                  the better tool and we will say so.
                </p>
                <TextLink href="https://visionify.ai" external className="also-from__link">
                  visionify.ai
                </TextLink>
              </div>
            </Card>
          </Reveal>
        </div>
      </Section>

      {/* ── Principles ─────────────────────────────────────────────────────── */}
      <Section tone="sunken">
        <SectionIntro
          eyebrow="How we build it"
          title="Four rules that decide arguments"
          lead="Every one of these has killed a feature somebody wanted. That is what makes them useful rather than decorative."
        />

        <ol className="principle-grid">
          {PRINCIPLES.map(({ title, body }, index) => (
            <Reveal as="li" key={title} delay={index * 60}>
              <div className="principle">
                <span aria-hidden className="step-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="blurb__title blurb__title--lg">{title}</h3>
                  <p className="blurb__body blurb__body--lg">{body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* ── Contact ───────────────────────────────────────────────────────── */}
      <Section id="contact" tone="panel" className="contact-band">
        <div className="split split--align-end">
          <SectionIntro
            tone="panel"
            eyebrow="Contact"
            title="Tell us what your crews actually argue about."
            lead="Pricing, a security review, a hazard we have not thought about, or a trade we have not listed. A real person answers, and it is usually somebody who wrote part of it."
          />

          <Reveal delay={80} className="contact-actions">
            <a href="mailto:support@visionify.ai" className="contact-actions__link">
              <Button variant="panel" size="lg" block icon={<Mail />}>
                support@visionify.ai
              </Button>
            </a>
            <Link to="/signup" className="contact-actions__link">
              <Button variant="panel-ghost" size="lg" block>
                Or just start free
              </Button>
            </Link>
            <p className="contact-hint">
              Signing up creates your company and first site in under a minute. You will not be
              waiting on us to get started.
            </p>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
